/**
 * Portal.js
 *
 * Copyright (c) apploft. GmbH
 */

import React, {useReducer, useEffect} from 'react';
import {
  LoginForm,
  OidcSignIn,
  stringMatching,
  useStrings,
  useColors,
} from 'apploft-cloud-forms';
import {APIDefinition} from 'apploft-cloud-client';
import {authorize, authorizeCallback} from './PortalEventSymbol';
import {reducer, init} from './PortalState';
import {authorizing, unauthorized, authorized, authorizingCallback} from './PortalStateSymbol';
import EndPointBrowser from '../EndPointBrowser/EndPointBrowser';
import './Portal.css';
import {LoginStrategy} from './LoginStrategy';

const DEFAULT_LOGIN_CONFIG = {
  strategy: LoginStrategy.CREDENTIALS,
};

function Message({string}) {
  return (
    <div className="portal">
      <h2>{string}</h2>
    </div>
  );
}

function Login({dispatch, api, strings, loginConfig}) {
  const loginComponent = (() => {
    switch (loginConfig.strategy) {
      case LoginStrategy.OIDC: return OidcLogin({oidcService: loginConfig.service, strings});
      case LoginStrategy.CREDENTIALS:
      default: return CredentialsLogin({dispatch, api, strings});
    }
  })();

  return (
    <div className="portal">
      {loginComponent}
    </div>
  );
}

function OidcLogin({oidcService, strings}) {
  return (
    <OidcSignIn
      className="loginForm"
      onClick={() => { oidcService.login(); }}
      strings={strings}
    />
  );
}

function CredentialsLogin({dispatch, api, strings}) {
  return (
    <LoginForm
      className="loginForm"
      onSubmit={formData => {
        dispatch({event: authorize, payload: {formData, dispatch, api}});
      }}
      strings={strings}
    />
  );
}

function AuthorizationCallback({dispatch, loginConfig, message}) {
  useEffect(() => {
    const controller = new AbortController();
    if (loginConfig.strategy === LoginStrategy.OIDC) {
      setTimeout(() => dispatch({
        event: authorizeCallback, payload: {
          authService: loginConfig.service,
          dispatch, controller,
        },
      }), 0);
    }
    return () => {
      controller.abort();
    };
  }, [dispatch, loginConfig.service, loginConfig.strategy]);
  return (<Message string={message} />);
}

function Portal({state, data, dispatch, api, strings, colors, loginConfig}) {
  switch (state) {
    case authorizing:
      return (
        <AuthorizationCallback
          dispatch={dispatch}
          loginConfig={loginConfig}
          message={stringMatching(strings, 'wait')}
        />
      );

    case authorizingCallback:
      return Message({string: stringMatching(strings, 'wait')});

    case unauthorized:
      return Login({dispatch, api, strings, loginConfig});

    case authorized:
      return EndPointBrowser({
        api: APIDefinition.enable({api, data}),
        strings,
        colors,
      });

    default:
      throw new Error('unrendered state');
  }
}

function Main({api, loginConfig = DEFAULT_LOGIN_CONFIG}) {
  const [portal, dispatch] = useReducer(reducer, null, init);

  const props = {
    state: portal.state,
    data: portal.data,
    api,
    dispatch,
    strings: useStrings(),
    colors: useColors(),
    loginConfig,
  };

  return <Portal {...props} />;
}

export default Main;
